import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	IssueNavigatorFeaturesContext,
	IssueNavigatorFeatureProviderProps,
	IssueNavigatorFeatureGuardProps,
} from './types.tsx';

const defaultFeatureContext: IssueNavigatorFeaturesContext = {
	isViewSwitcherEnabled: true,
	isOnboardingEnabled: true,
	isExportMenuEnabled: true,
	isRowContextMenuEnabled: true,
	isSaveFilterButtonEnabled: true,
	isBulkChangeWorkItemsEnabled: true,
	isImportWorkItemsFromCSVEnabled: true,
};

const IssueNavigatorFeatureContext =
	createContext<IssueNavigatorFeaturesContext>(defaultFeatureContext);

const useIssueNavigatorFeatures = () => useContext(IssueNavigatorFeatureContext);

const useIssueNavigatorFeature = (featureKey: keyof IssueNavigatorFeaturesContext) =>
	useIssueNavigatorFeatures()[featureKey];

const IssueNavigatorFeatureProvider = ({
	children,
	...props
}: IssueNavigatorFeatureProviderProps) => {
	const value = useMemo(() => ({ ...defaultFeatureContext, ...props }), [props]);

	return (
		<IssueNavigatorFeatureContext.Provider value={value}>
			{children}
		</IssueNavigatorFeatureContext.Provider>
	);
};

const IssueNavigatorFeatureGuard = ({ featureKey, on, off }: IssueNavigatorFeatureGuardProps) => {
	const feature = useIssueNavigatorFeature(featureKey);
	return feature ? on : off;
};

type IssueNavigatorFeatureGuardGatedProps = IssueNavigatorFeatureGuardProps & {
	fallback: ReactNode;
};

const IssueNavigatorFeatureGuardGated = ({
	fallback,
	...props
}: IssueNavigatorFeatureGuardGatedProps) => {
	return fg('jira_nin_smart_link') ? <IssueNavigatorFeatureGuard {...props} /> : fallback;
};

export {
	IssueNavigatorFeatureProvider,
	useIssueNavigatorFeatures,
	useIssueNavigatorFeature,
	IssueNavigatorFeatureGuardGated as IssueNavigatorFeatureGuard,
};
